import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import { savePersonalInternalSubtaskTemplate } from '../changePersonalInternalSubtaskTemplate';
import {
  persistInternalSubtaskTemplateFromDraft,
  deletePersistedInternalSubtaskTemplate,
  type InternalSubtaskTemplatePersistMode,
} from '../changePersistInternalSubtaskTemplate';
import {
  INSTANCE_CHECKLIST_FIELD_TYPES,
  createInstanceChecklistFieldApi,
  templateFieldsToReviewQuestions,
  type ReviewQuestionDraft,
} from '../changeInstanceChecklistFields';
import ChangeFormFieldBuilder from './ChangeFormFieldBuilder';

interface Props {
  questions: ReviewQuestionDraft[];
  templateId?: number | null;
  formTabs?: string[];
  onChange: (next: ReviewQuestionDraft[]) => void;
  groupId?: number;
  defaultModelName?: string;
  templatePersistMode?: InternalSubtaskTemplatePersistMode | null;
  onTemplateSaved?: (templateId: number, groupId: number) => void;
  onTemplateDeleted?: (templateId: number) => void;
  canDeletePersistedTemplate?: boolean;
}

const ChangeInstanceChecklistEditor: React.FC<Props> = ({
  questions,
  templateId,
  formTabs,
  onChange,
  groupId,
  defaultModelName = '',
  templatePersistMode = null,
  onTemplateSaved,
  onTemplateDeleted,
  canDeletePersistedTemplate = false,
}) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [builderKey, setBuilderKey] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [modelName, setModelName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const seed = defaultModelName.trim();
    if (!seed) return;
    setModelName((prev) => (prev.trim() ? prev : seed));
  }, [defaultModelName, templateId]);

  const api = useMemo(
    () =>
      createInstanceChecklistFieldApi(questions, (next) => {
        onChangeRef.current(next);
      }),
    [builderKey],
  );

  const loadFromTemplate = () => {
    if (!templateId) return;
    setImportError(null);
    void changesApi.listInternalSubtaskTemplateFields(templateId).then(
      (data) => {
        const items = (data.items || []).filter((field) =>
          INSTANCE_CHECKLIST_FIELD_TYPES.has(field.field_type),
        );
        if (!items.length) {
          setImportError('O modelo não tem campos extras para copiar.');
          return;
        }
        const next = templateFieldsToReviewQuestions(templateId, items);
        onChangeRef.current(next);
        setBuilderKey((value) => value + 1);
      },
      () => {
        setImportError('Não foi possível carregar os campos do modelo.');
      },
    );
  };

  const persistMode = templatePersistMode;
  const isGlobal = persistMode === 'global';
  const saveLabel = isGlobal ? 'Salvar modelo (cadastro)' : 'Salvar meu modelo';
  const blockTitle = isGlobal ? 'Modelo reutilizável (cadastro)' : 'Meu modelo pessoal';
  const blockHelp = isGlobal
    ? 'Grava no Cadastros de subtarefas internas e fica disponível para todos nas modais de disparo (somente gestores editam ou excluem).'
    : 'Grava só para sua conta: reaparece ao reabrir a modal. Não aparece no Cadastros.';

  const runSave = () => {
    if (!groupId || !persistMode) return;
    setSaveError(null);
    setSaving(true);
    const name = modelName.trim();
    const savePromise =
      persistMode === 'personal'
        ? savePersonalInternalSubtaskTemplate({
            name,
            groupId,
            questions,
            templateId: templateId && templateId > 0 ? templateId : undefined,
          })
        : persistInternalSubtaskTemplateFromDraft({
            name,
            groupId,
            questions,
            mode: 'global',
            templateId: templateId && templateId > 0 ? templateId : undefined,
          });
    void savePromise
      .then((savedId) => {
        setModelName('');
        onTemplateSaved?.(savedId, groupId);
      })
      .catch((err: unknown) => {
        setSaveError(err instanceof Error ? err.message : 'Não foi possível salvar o modelo.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <p className="small text-muted mb-2">
        {persistMode
          ? 'Monte os campos do checklist e salve como modelo reutilizável.'
          : 'Monte os campos do checklist para esta seleção na mudança.'}
      </p>
      {templateId ? (
        <Button
          type="button"
          size="sm"
          variant="outline-secondary"
          className="mb-2 me-2"
          onClick={loadFromTemplate}
        >
          Recarregar campos do modelo
        </Button>
      ) : null}
      {importError ? (
        <Alert variant="warning" className="small py-2">
          {importError}
        </Alert>
      ) : null}
      {persistMode && groupId ? (
        <div className="border rounded p-2 mb-3 bg-light">
          <div className="small fw-semibold mb-1">{blockTitle}</div>
          <p className="small text-muted mb-2">{blockHelp}</p>
          <Form.Label htmlFor="persist-internal-template-name" className="small">
            Nome do modelo
          </Form.Label>
          <Form.Control
            id="persist-internal-template-name"
            size="sm"
            value={modelName}
            onChange={(event) => setModelName(event.target.value)}
            placeholder="Ex.: Checklist Nuvem"
            className="mb-2"
          />
          {saveError ? (
            <Alert variant="danger" className="small py-2">
              {saveError}
            </Alert>
          ) : null}
          <div className="d-flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline-primary"
              disabled={saving || !modelName.trim()}
              onClick={runSave}
            >
              {saving ? 'Salvando…' : saveLabel}
            </Button>
            {canDeletePersistedTemplate && templateId && templateId > 0 ? (
              <Button
                type="button"
                size="sm"
                variant="outline-danger"
                disabled={deleting}
                onClick={() => {
                  setSaveError(null);
                  setDeleting(true);
                  void deletePersistedInternalSubtaskTemplate(templateId)
                    .then(() => {
                      onTemplateDeleted?.(templateId);
                    })
                    .catch(() => {
                      setSaveError('Não foi possível excluir o modelo.');
                    })
                    .finally(() => setDeleting(false));
                }}
              >
                {deleting ? 'Excluindo…' : 'Excluir modelo'}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <ChangeFormFieldBuilder
        key={builderKey}
        embedded
        heading=""
        description=""
        api={api}
        formTabs={formTabs}
        allowDateRevisionHistory
      />
    </div>
  );
};

export default ChangeInstanceChecklistEditor;
